import React, { useEffect, useState } from "react";
import ContentPageTitle from "../../../common/ContentPageTitle";
import { useManagePromoteVideos } from "../../../hooks/useManagePromoteVideos";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
import axiosWithAuth from "../../../utils/axiosWithAuth";
import { Button, Modal, Form, Input, message, Checkbox } from "antd";

export default function PromoteVideos(props) {
  const { business } = props;

  const [promoteVideos, setPromoteVideos] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    console.log("business", business);
    getAllPromoteVideos();
  }, [business]);

  const getAllPromoteVideos = async () => {
    try {
      const response = await axiosWithAuth().get(
        `/api/business/media/list/${business.id}/2`,
      );
      setPromoteVideos(response.data);
    } catch (error) {
      console.error("Error getting videos to promote:", error);
    }
  };

  // Custom hook to manage the creation/deletion of business listing
  const hookData = {
    dispatchAction: getAllPromoteVideos,
    itemType: "video",
    deleteItemAPI: "/api/business/mediaBusiness",
    business: business,
    modalData: {
      title: "Create New Video to Promote",
      fieldLabel: "Video to Promote Name",
    },
  };

  const [modalComponent, editModalComponent, CreateItemButton, ListComponent] =
    useManagePromoteVideos(hookData);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    console.log("Form values:", values);
    setLoading(true);
    try {
      const response = await axiosWithAuth().post(
        "/api/activity/sendMailRequestUploadMedia",
        {
          nameUser: values.name,
          emailUser: values.email,
          titleMedia: values.titleMedia,
          youtubeLink: values.youtubeLink,
          descriptionMedia: values.description,
          FEIBusiness:
            business.meta_data?.find(
              (meta) => meta.meta_key === "business_fei_ein_number",
            )?.meta_value || "",
        },
      );

      message.success("YouTube video submitted successfully. It is pending administrator approval.");
      setIsModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error("Error sending video upload request:", error);
      message.error("Error sending the request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <ContentPageTitle title="Videos to Promote" icon="video" />

      <div className="content-box">
        <Routes>
          <Route
            path="/"
            element={
              <>
                <div
                  style={{
                    marginBottom: "30px",
                    textAlign: "center",
                    padding: "20px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "8px",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <h3 style={{ marginBottom: "15px", color: "#1890ff" }}>
                    Submit Your YouTube Video
                  </h3>
                  <p style={{ marginBottom: "20px", color: "#666" }}>
                    Paste the link to your YouTube video and submit it for
                    approval. Our administrator will review your video. Once
                    approved, it will appear in your video promotions list below.
                  </p>
                  <Button
                    type="primary"
                    size="large"
                    onClick={showModal}
                    style={{
                      height: "45px",
                      fontSize: "16px",
                      padding: "0 30px",
                    }}
                  >
                    Submit YouTube Video
                  </Button>
                </div>

                <ListComponent
                  emptyMessage="You haven't created any video promotions yet. Submit your YouTube video link above for administrator approval to get started."
                  btnText="Create New Video to Promote"
                  listItems={promoteVideos.data}
                />
              </>
            }
            exact
          />
        </Routes>
      </div>

      <Modal
        title="Submit Your YouTube Video"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[
              {
                required: true,
                message: "Please enter your name",
              },
            ]}
          >
            <Input placeholder="Enter your full name" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                required: true,
                message: "Please enter your email",
              },
              {
                type: "email",
                message: "Please enter a valid email address",
              },
            ]}
          >
            <Input placeholder="example@email.com" />
          </Form.Item>

          <Form.Item
            name="titleMedia"
            label="Video Title"
            rules={[
              {
                required: true,
                message: "Please enter a video title",
              },
            ]}
          >
            <Input placeholder="Enter video title" />
          </Form.Item>

          <Form.Item
            name="youtubeLink"
            label="YouTube Video Link"
            rules={[
              {
                required: true,
                message: "Please enter the YouTube video link",
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();

                  try {
                    const url = new URL(value);
                    const hostname = url.hostname
                      .replace(/^www\./, "")
                      .toLowerCase();
                    const pathParts = url.pathname.split("/").filter(Boolean);
                    const isShortUrl =
                      hostname === "youtu.be" && pathParts.length > 0;
                    const isYouTubeUrl = [
                      "youtube.com",
                      "m.youtube.com",
                      "music.youtube.com",
                      "youtube-nocookie.com",
                    ].includes(hostname);
                    const isVideoPath =
                      (url.pathname === "/watch" && url.searchParams.has("v")) ||
                      (["shorts", "live", "embed"].includes(pathParts[0]) &&
                        pathParts.length > 1);

                    if (
                      url.protocol === "https:" &&
                      (isShortUrl || (isYouTubeUrl && isVideoPath))
                    ) {
                      return Promise.resolve();
                    }
                  } catch (error) {
                    // The validation message below handles malformed URLs.
                  }

                  return Promise.reject(
                    new Error("Please enter a valid YouTube video link"),
                  );
                },
              },
            ]}
          >
            <Input placeholder="https://www.youtube.com/watch?v=..." />
          </Form.Item>

          <Form.Item
            name="description"
            label="Video Description"
            rules={[
              {
                required: true,
                message: "Please describe the video you want to submit",
              },
              {
                min: 1,
                message: "Description cannot be empty",
              },
            ]}
          >
            <Input.TextArea
              rows={4}
              placeholder="Describe the video content, its purpose and any relevant information we should know..."
              showCount
              maxLength={500}
            />
          </Form.Item>

          <div
            style={{
              marginTop: "8px",
              padding: "8px 12px",
              backgroundColor: "#fff7e6",
              border: "1px solid #ffd591",
              borderRadius: "4px",
              fontSize: "13px",
              color: "#d46b08",
              marginBottom: "16px",
            }}
          >
            <strong>Important:</strong> Videos longer than 2 minutes will not be
            accepted. Please ensure your video content is concise and engaging.
          </div>

          <Form.Item
            name="termsAccepted"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("You must accept the terms and conditions"),
                      ),
              },
            ]}
          >
            <Checkbox>
              <div style={{ fontSize: "14px", lineHeight: "1.4" }}>
                By submitting your video link, you confirm you own this video, it contains only
                professional, business-related content, and it does not infringe
                any rights. Political, gossip, obscene, or discriminatory
                material is prohibited. BIZZLL may reject or remove content at
                its discretion.{" "}
                <a
                  href="http://video.bizzll.com/wp-content/uploads/2025/08/BIZZLL-Video-Submission-Requirements-Guidelines.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#1890ff", textDecoration: "underline" }}
                >
                  Read Video Rules
                </a>
              </div>
            </Checkbox>
          </Form.Item>

          <Form.Item>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <Button onClick={handleCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Submit for Approval
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
      {modalComponent}
      {editModalComponent}
    </div>
  );
}
